import { useEffect, useState } from "react";
import { useApp } from "../state/AppContext";
import { useLabels, useViews } from "../state/hooks";
import { api } from "../api/client";

interface Counts {
  inbox: number;
  tiers: { now: number; vendor: number; fyi: number };
}

const LABEL_COLOR_VAR: Record<string, string> = {};

export function Sidebar({
  view,
  onSelectView,
  onCompose,
  onOpenSettings,
}: {
  view: string;
  onSelectView: (v: string) => void;
  onCompose: () => void;
  onOpenSettings: () => void;
}) {
  const { me, currentMailbox, setCurrentMailboxId, signOut } = useApp();
  const { views } = useViews(currentMailbox?.id ?? null);
  const { labels } = useLabels(currentMailbox?.id ?? null);
  const [counts, setCounts] = useState<Counts>({ inbox: 0, tiers: { now: 0, vendor: 0, fyi: 0 } });
  const [switcherOpen, setSwitcherOpen] = useState(false);

  useEffect(() => {
    if (!currentMailbox) return;
    api.get<Counts>(`/api/mailboxes/${currentMailbox.id}/counts`).then(setCounts).catch(() => {});
  }, [currentMailbox, view]);

  if (!me || !currentMailbox) return null;

  return (
    <div className="gm-sidebar">
      <div className="gm-brand">
        <div className="gm-brand-mark">G</div>
        <div className="gm-brand-name">GeerMail</div>
      </div>

      {me.mailboxes.length > 1 && (
        <div style={{ position: "relative" }}>
          <button className="gm-mailbox-switcher" onClick={() => setSwitcherOpen((v) => !v)}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentMailbox.displayName ?? currentMailbox.email}</span>
            <span style={{ marginLeft: "auto", opacity: 0.6 }}>⌄</span>
          </button>
          {switcherOpen && (
            <div className="card card-raised" style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 10, padding: 4 }}>
              {me.mailboxes.map((m) => (
                <button
                  key={m.id}
                  className="gm-nav-row"
                  data-active={m.id === currentMailbox.id}
                  onClick={() => {
                    setCurrentMailboxId(m.id);
                    setSwitcherOpen(false);
                  }}
                >
                  {m.email}
                  <span className="gm-nav-count">{m.kind === "shared" ? "shared" : "personal"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <button className="btn btn-primary btn-block" onClick={onCompose}>
        Compose <span className="hint">C</span>
      </button>

      <div className="gm-nav-section">
        <button className="gm-nav-row" data-active={view === "now"} onClick={() => onSelectView("now")}>
          <span className="gm-tier-bar" style={{ background: "var(--lb-red-2)" }} />
          Needs you now
          <span className="gm-nav-count">{counts.tiers.now}</span>
        </button>
        <button className="gm-nav-row" data-active={view === "vendor"} onClick={() => onSelectView("vendor")}>
          <span className="gm-tier-bar" style={{ background: "var(--lb-amber-2)" }} />
          Waiting on vendor
          <span className="gm-nav-count">{counts.tiers.vendor}</span>
        </button>
        <button className="gm-nav-row" data-active={view === "fyi"} onClick={() => onSelectView("fyi")}>
          <span className="gm-tier-bar" style={{ background: "var(--lb-blue-2)" }} />
          FYI
          <span className="gm-nav-count">{counts.tiers.fyi}</span>
        </button>
      </div>

      <div className="gm-nav-section">
        <div className="section-header" style={{ padding: "0 7px 2.8px" }}>Views</div>
        {views
          .filter((v) => !v.hidden)
          .map((v) => (
            <button
              key={v.id}
              className="gm-nav-row"
              data-active={view === (v.builtinKey ?? v.id)}
              onClick={() => onSelectView(v.builtinKey ?? v.rule ?? v.id)}
            >
              {v.name}
              {v.builtinKey === "inbox" && <span className="gm-nav-count">{counts.inbox}</span>}
            </button>
          ))}
      </div>

      <div className="gm-nav-section">
        <div className="section-header" style={{ padding: "0 7px 2.8px" }}>Labels</div>
        {labels.map((l) => (
          <button
            key={l.id}
            className="gm-nav-row"
            style={{ paddingLeft: l.parentLabelId ? 20 : 7 }}
            data-active={view === `label:${l.id}`}
            onClick={() => onSelectView(`label:${l.id}`)}
          >
            <span style={{ width: 6, height: 6, borderRadius: 2, background: LABEL_COLOR_VAR[l.color] ?? l.color, flexShrink: 0 }} />
            {l.name}
            <span className="gm-nav-count">{l.threadCount}</span>
          </button>
        ))}
      </div>

      <div className="gm-sidebar-footer">
        <button className="gm-nav-row" onClick={onOpenSettings}>
          Settings <span className="hint">⌘,</span>
        </button>
        <button className="gm-nav-row" onClick={() => signOut()}>
          Sign out
        </button>
      </div>
    </div>
  );
}
